import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../Sidebar/DashboardLayout';
import '../Table.css';
import GenericTable from '../../commun/GenericTable';
import axios from 'axios';
import { showDeleteConfirmation } from '../../commun/deletemsg';
import Pagination from '../../commun/Pagination';
import TableFilterControls from '../../commun/TableFilterControls';
import ActionButtons from '../../commun/ActionButtons';
import Chatbox from '../../commun/ChatBox';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const MissionsDashboard = () => {
  const [displayMode, setDisplayMode] = useState('table');
  const [missions, setMissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const itemsPerPage = 5;
  const [editingMissionId, setEditingMissionId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    nomMission: '',
    nomEntreprise: '',
  });
  const userRole = localStorage.getItem('userType');

  useEffect(() => {
    const fetchMissions = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/missions/all`);
        setMissions(response.data);
      } catch (error) {
        console.error('Erreur de récupération des missions:', error);
      }
    };
    fetchMissions();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  const handleEdit = (missionId) => {
    const missionToEdit = missions.find((m) => m._id === missionId);
    setEditingMissionId(missionId);
    setEditFormData({
      nomMission: missionToEdit.nomMission,
      nomEntreprise: missionToEdit.createdBy?.NomEntreprise || '',
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        nomMission: editFormData.nomMission,
        createdBy: { NomEntreprise: editFormData.nomEntreprise }
      };

      await axios.put(`${API_BASE}/api/missions/${editingMissionId}`, payload);

      const updatedMissions = missions.map((mission) =>
        mission._id === editingMissionId
          ? { ...mission, nomMission: payload.nomMission, createdBy: { NomEntreprise: payload.createdBy.NomEntreprise } }
          : mission
      );
      setMissions(updatedMissions);
      setEditingMissionId(null);
    } catch (error) {
      console.error('Erreur mise à jour mission:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingMissionId(null);
  };

  const handleToggleStatus = useCallback(async (missionId) => {
    const mission = missions.find(m => m._id === missionId);
    const newStatus = mission.status === 'active' ? 'inactive' : 'active';

    const updatedMissions = missions.map(m =>
      m._id === missionId ? { ...m, status: newStatus } : m
    );
    setMissions(updatedMissions);

    try {
      await axios.put(`${API_BASE}/api/missions/${missionId}`, { status: newStatus });
    } catch (error) {
      console.error('Erreur mise à jour statut mission:', error);
      setMissions(missions);
    }
  }, [missions]);

  const handleDelete = (missionId) => {
    showDeleteConfirmation({
      onConfirm: async () => {
        try {
          await axios.delete(`${API_BASE}/api/missions/${missionId}`);
          const updatedMissions = missions.filter(m => m._id !== missionId);
          setMissions(updatedMissions);
        } catch (error) {
          console.error('Erreur suppression mission:', error);
        }
      },
      onCancel: () => {},
    });
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids.length) return;
    showDeleteConfirmation({
      onConfirm: async () => {
        try {
          for (const id of ids) {
            await axios.delete(`${API_BASE}/api/missions/${id}`);
          }
          setMissions(prev => prev.filter(m => !ids.includes(m._id)));
          setSelectedRows([]);
        } catch (error) {
          console.error('Erreur suppression groupée:', error);
        }
      }
    });
  };

  const filteredMissions = useMemo(() =>
    missions.filter((mission) => {
      const nameMatch = mission.nomMission?.toLowerCase().includes(searchTerm.toLowerCase());
      const statusMatch = statusFilter ? mission.status === statusFilter : true;
      return nameMatch && statusMatch;
    }),
    [missions, searchTerm, statusFilter]
  );

  const sortedMissions = useMemo(() =>
    [...filteredMissions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [filteredMissions]
  );

  const columns = [
    {
      key: 'nomMission',
      label: 'Nom mission',
      render: (value, row) =>
        editingMissionId === row._id ? (
          <input
            type="text"
            name="nomMission"
            value={editFormData.nomMission}
            onChange={handleEditFormChange}
          />
        ) : (
          row.nomMission
        )
    },
    {
      key: 'nomEntreprise',
      label: 'Nom entreprise',
      render: (value, row) =>
        editingMissionId === row._id ? (
          <input
            type="text"
            name="nomEntreprise"
            value={editFormData.nomEntreprise}
            onChange={handleEditFormChange}
          />
        ) : (
          row.nomEntreprise
        )
    },
    {
      key: 'createdAt',
      label: 'Date de création',
      render: (value) => new Date(value).toLocaleDateString('fr-FR'),
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value, row) => (
        <div
          className={row.status === 'active' ? 'statusActive' : 'statusInactive'}
          onClick={() => handleToggleStatus(row._id)}
          style={{ cursor: 'pointer' }}
        >
          {row.status === 'active' ? 'Active' : 'Inactive'}
        </div>
      )
    }
  ];

  const tableData = sortedMissions.map((m) => ({
    nomMission: m.nomMission,
    nomEntreprise: m.NomEntreprise || m.createdBy?.NomEntreprise || 'N/A',
    createdAt: m.createdAt,
    status: m.status,
    _id: m._id
  }));

  return (
    <section className="dashboardContainer">
      <div className="headerTalentWrapper">
        <h3 className="componentTitle">Missions postées</h3>
        <div className="top-controlsC">
          <TableFilterControls
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            statusValue={statusFilter}
            onStatusChange={setStatusFilter}
          />
        </div>
      </div>

      <GenericTable
      
        columns={columns}
        data={tableData}
        totalItems={missions.length}
        renderActions={(row) => (
          <ActionButtons
            isEditing={editingMissionId === row._id}
            onEdit={() => handleEdit(row._id)}
            onDelete={() => handleDelete(row._id)}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
            showView={false}
            
          />
        )}
        currentPage={page}
        itemsPerPage={itemsPerPage}
        setPage={setPage}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        onDeleteSelected={handleDeleteSelected}
        showDeleteButton={true}
        showCheckboxes={true}
        displayMode={displayMode}          
         setDisplayMode={setDisplayMode}
      />

      <Pagination
        page={page}
        setPage={setPage}
        totalItems={tableData.length}
        itemsPerPage={itemsPerPage}
      />
      {userRole !== 'agent' && <Chatbox userRole={userRole} />}
      </section>
  );
};

const Missions = () => (
  <DashboardLayout>
    <MissionsDashboard />
  </DashboardLayout>
);

export default Missions;
